import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import { useDeleteComponent } from './useDeleteComponent';
import styles from './DeleteComponentDialog.module.css';

interface DeleteComponentDialogProps {
  componentId: string;
  componentTitle: string;
  componentUid?: number | string | null;
  currentCourseId: string;
  isLive?: boolean;
  itemKind?: 'chapter' | 'divider';
  onOpenChange(open: boolean): void;
  onSuccess(message: string): void;
  open: boolean;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : 'Unable to delete this chapter right now.';
}

export function DeleteComponentDialog({
  componentId,
  componentTitle,
  componentUid,
  currentCourseId,
  isLive = false,
  itemKind = 'chapter',
  onOpenChange,
  onSuccess,
  open,
}: DeleteComponentDialogProps) {
  const deleteComponent = useDeleteComponent(currentCourseId);
  const isDivider = itemKind === 'divider';

  async function handleDelete() {
    await deleteComponent.mutateAsync({
      componentId,
      componentUid,
    });
    onSuccess(`Deleted ${componentTitle}`);
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div>
            <Dialog.Title className={styles.title}>
              Delete "{componentTitle}"?
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                {isDivider
                  ? 'This permanently removes the section divider. The chapters below it stay in the course and move up into the previous section.'
                  : 'This permanently deletes the chapter and all of its content from this course. This cannot be undone.'}
              </p>
            </Dialog.Description>
          </div>

          {isLive && !isDivider ? (
            <div className={styles.warning} role="alert">
              <strong>This chapter is live.</strong>
              <span>
                Student submissions and progress will be lost if you delete it.
              </span>
            </div>
          ) : null}

          {deleteComponent.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {getErrorMessage(deleteComponent.error)}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button disabled={deleteComponent.isPending} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              destructive
              disabled={deleteComponent.isPending}
              onClick={() => {
                void handleDelete();
              }}
            >
              {deleteComponent.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
